import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQueryClient } from "@tanstack/vue-query";
import {
  PaymentTokensApi,
  type V2TokensPaymentPostRequest,
  type V2TokensPaymentSessionPostRequest,
} from "mws-ts-v2-sdk";

const getPaymentTokensApi = () => {
  return new PaymentTokensApi(
    undefined,
    useRuntimeConfig().public.financeServiceUrl,
  );
};

export const useCreatePaymentSessionForTokenisationMutation = () =>
  useMutation({
    mutationFn: async (request: V2TokensPaymentSessionPostRequest) => {
      const response = await getPaymentTokensApi().v2TokensPaymentSessionPost({
        v2TokensPaymentSessionPostRequest: request,
      });

      return response.data;
    },
  });

export const usePostTokenisationPaymentMutation = () =>
  useMutation({
    mutationFn: async (request: V2TokensPaymentPostRequest) => {
      const response = await getPaymentTokensApi().v2TokensPaymentPost({
        v2TokensPaymentPostRequest: request,
      });

      return response.data;
    },
  });

export const tokensQueryKeys = createQueryKeys("tokens", {
  list: (offset?: MaybeRef<number>, pageSize?: MaybeRef<number>) => ({
    queryKey: ["list"],
    queryFn: async () => {
      const response = await getPaymentTokensApi().v2TokensGet({
        offset: unref(offset),
        pageSize: unref(pageSize),
      });

      return response.data;
    },
  }),
});

export const useDeletePaymentTokenMutation = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const i18n = useI18n();

  const onSuccess = () => {
    toast.success(i18n.t("myAccount.payment.deleteSuccess"));

    queryClient.invalidateQueries({
      queryKey: tokensQueryKeys.list().queryKey,
    });
  };

  const onError = (error: unknown) => {
    toast.error(getErrorMessage(error)[0]);
  };

  return useMutation({
    mutationFn: async (paymentTokenId: string) => {
      const response = await getPaymentTokensApi().v2TokensPaymentTokenIdDelete(
        { paymentTokenId },
      );

      return response.data;
    },
    onSuccess,
    onError,
  });
};

export const useSetDefaultPaymentTokenMutation = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const i18n = useI18n();
  const onSuccess = () => {
    toast.success(i18n.t("myAccount.payment.setDefaultSuccess"));

    queryClient.invalidateQueries({
      queryKey: tokensQueryKeys.list().queryKey,
    });
  };

  const onError = (error: unknown) => {
    toast.error(getErrorMessage(error)[0]);
  };

  return useMutation({
    mutationFn: async (paymentTokenId: string) => {
      const response =
        await getPaymentTokensApi().v2TokensPaymentTokenIdSetDefaultPut({
          paymentTokenId,
        });

      return response.data;
    },
    onSuccess,
    onError,
  });
};
